/**
 * "Create task from message" — V7-04 §4, the round trip's near half.
 *
 * The server prefills (first line as the title, the whole text plus a permanent
 * backlink as the description, the author as the suggested assignee) and this
 * modal lets a person change all of it before anything is created. The project
 * is the one thing with no sensible default: a server that guessed would put
 * work on somebody's board that nobody chose to put there.
 *
 * The task is created through the ordinary `POST /tasks`, deliberately. A
 * second creation path would be a second place for project permissions,
 * required fields, automations and activity to drift; this one inherits all of
 * it, and then asks chat to post the chip.
 */

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProjects } from '@/features/projects/api';
import { useCreateTask } from '@/features/tasks/api';
import { usePostTaskChip, useTaskDraft } from './api';

export function CreateTaskFromMessage({
  messageId,
  onClose,
}: {
  /** Null closes the dialog; a message id opens it and loads that draft. */
  messageId: string | null;
  onClose: () => void;
}) {
  const draft = useTaskDraft(messageId);
  const projects = useProjects();
  const createTask = useCreateTask();
  const postChip = usePostTaskChip();

  const [title, setTitle] = useState('');
  const [projectId, setProjectId] = useState('');

  // The draft arrives after the dialog opens, so the title is filled when it
  // does — and only then, or typing would be overwritten on every render.
  useEffect(() => {
    if (draft.data) setTitle(draft.data.title);
  }, [draft.data]);

  const submit = () => {
    if (!draft.data || !projectId) return;

    createTask.mutate(
      {
        projectId,
        title: title.trim(),
        description: draft.data.description,
        assigneeIds: draft.data.suggestedAssigneeId ? [draft.data.suggestedAssigneeId] : [],
      },
      {
        onSuccess: (task) => {
          /**
           * The chip is posted after the task exists, and its failure does not
           * undo the task. A task created without its chip is a task; a chip
           * pointing at nothing is a lie, which is why the order is this way
           * round rather than the other.
           */
          postChip.mutate(
            { messageId: draft.data!.sourceMessageId, taskId: task._id },
            {
              onError: () =>
                toast.warning(`${task.key} created, but its chip did not post to the thread.`),
            },
          );
          toast.success(`${task.key} created from the message.`);
          onClose();
        },
        onError: (error) => toast.error(messageOf(error)),
      },
    );
  };

  return (
    <Dialog open={Boolean(messageId)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-task-from-message">
        <DialogTitle>New task from message</DialogTitle>
        <DialogDescription className="sr-only">
          Create a task prefilled from this chat message. A link back to the message is kept in the
          description.
        </DialogDescription>

        {draft.isLoading && <p className="py-6 text-center text-[12px] vt-muted">Loading…</p>}

        {draft.data && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="task-from-message-title">Title</Label>
              <Input
                id="task-from-message-title"
                data-testid="task-from-message-title"
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="task-from-message-project">Project</Label>
              <select
                id="task-from-message-project"
                data-testid="task-from-message-project"
                className="h-8 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={projectId}
                onChange={(event) => setProjectId(event.target.value)}
              >
                <option value="">Choose a project…</option>
                {projects.data?.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <p className="text-[11px] vt-muted" data-testid="task-from-message-backlink-note">
              The message text and a link back to it are kept in the task’s description.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                data-testid="task-from-message-submit"
                disabled={!title.trim() || !projectId || createTask.isPending}
                onClick={submit}
              >
                {createTask.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Create task
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function messageOf(error: unknown): string {
  const detail = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return detail ?? 'Something went wrong.';
}
